/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import produce from 'immer';
import { convertChannelCasing } from 'lib-shared-api-utils';
import type {
  CreateSavedSearchApiResponse,
  DeleteSavedSearchApiResponse,
  ReadAdvancedSearchResultsApiResponse,
  ReadSavedSearchApiResponse,
  ReadSavedSearchListApiResponse,
  ReadSearchApiArg,
  ReadSearchApiResponse,
  ReadSearchEnrichedFiltersApiResponse,
  SavedSearch,
  SearchFilters,
  SearchScope,
  UpdateSavedSearchApiResponse,
} from 'lib-shared-types-api';
import {
  searchParamsToURLSearchParams,
  searchStringToParams,
} from 'lib-shared-url';

import { filtersInitialState } from 'app/views/layout/views/search/components/filters/utils/definitions';
import { enrichStaticFilters } from 'app/views/layout/views/search/utils/filters/enrichStaticFilters';

import { createReadSearchResultsSearchParams } from './transformArg';

/**
 * Transforms the endpoint responses of search endpoints.
 *
 * @param response The raw response.
 */
function transformSearchResultsResponse<
  TReadSearchApiResponse extends ReadSearchApiResponse,
>(response: any): TReadSearchApiResponse {
  transformSearchResultsAttributes(response);

  return {
    data: {
      // `v2/search` endpoint can return multiple scopes at a time. However
      // because we define the arg for that endpoint that you can only request
      // one scope at a time, the raw response will always have a single entry
      // in the result. Hence why we can safely take the first entry, which
      // would correspond with the scope defined by the arg.
      results: response.data[0],
    },
    meta: response.meta,
  } as TReadSearchApiResponse;
}

/**
 * Transforms the read search results endpoint response.
 *
 * @param response The response from the endpoint.
 * @param arg The arg used to call the endpoint.
 */
export function transformReadSearchResultsResponse<
  TReadSearchApiResponse extends ReadSearchApiResponse,
>(
  response: any,
  arg: ReadSearchApiArg | string,
  scope: SearchScope,
): TReadSearchApiResponse {
  if (!response?.data?.length) {
    return transformSearchResultsResponse({
      data: [
        {
          hits: [],
          hits_type: scope,
          exhaustive_facets_count: null,
          processing_time_ms: 0,
          hits_per_page: 0,
          nb_hits: 0,
          nb_pages: 0,
          page: 0,
          facets: null,
          query: '',
          params: '',
        },
      ],
      meta: { next_page: null, prev_page: null },
    });
  }

  const { nb_pages, page, hits_per_page: pageSize, query } = response.data[0];
  const searchParams =
    typeof arg === 'string'
      ? searchStringToParams(arg.split('?')[1])
      : createReadSearchResultsSearchParams(scope, arg);

  return {
    ...transformSearchResultsResponse(response),
    meta: {
      next_page:
        page < nb_pages - 1
          ? `/v2/search?${searchParamsToURLSearchParams({
              ...searchParams,
              query: query ?? '',
              pageSize,
              page: page + 1,
            })}`
          : null,
      prev_page:
        page > 0
          ? `/v2/search?${searchParamsToURLSearchParams({
              ...searchParams,
              query: query ?? '',
              pageSize,
              page: page - 1,
            })}`
          : null,
    },
  };
}

/**
 * Transforms the read advanced search results endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadAdvancedSearchResultsResponse(
  response: any,
): ReadAdvancedSearchResultsApiResponse {
  transformSearchResultsAttributes(response);

  return {
    data: {
      results: response.data.map((result) => ({
        ...result,
        nb_pages: result.nb_pages,
        // Pagination is 1-based in the frontend and 0-based in the backend
        page: result.page + 1,
      })),
    },
    meta: response.meta,
  };
}

/**
 * Transforms the read saved search list endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadSavedSearchListResponse(
  response: any,
): ReadSavedSearchListApiResponse {
  const data = response.data.saved_searches ?? response.data;

  return {
    data: {
      saved_searches: data.map(transformSavedSearch),
    },
    meta: response.meta,
  };
}

/**
 * Transforms the create saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformCreateSavedSearchResponse(
  response: any,
): CreateSavedSearchApiResponse {
  return {
    data: {
      saved_search: transformSavedSearch(
        response.data.saved_search ?? response.data,
      ),
    },
    meta: response.meta,
  };
}

/**
 * Transforms the read saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformReadSavedSearchResponse(
  response: any,
): ReadSavedSearchApiResponse {
  return {
    data: {
      saved_search: transformSavedSearch(
        response.data.saved_search ?? response.data,
      ),
    },
    meta: response.meta,
  };
}

/**
 * Transforms the update saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformUpdateSavedSearchResponse(
  response: any,
): UpdateSavedSearchApiResponse {
  return {
    data: {
      saved_search: transformSavedSearch(
        response.data.saved_search ?? response.data,
      ),
    },
    meta: response.meta,
  };
}

/**
 * Transforms the delete saved search endpoint response.
 *
 * @param response The response from the endpoint.
 */
export function transformDeleteSavedSearchResponse(
  response: any,
): DeleteSavedSearchApiResponse {
  return {
    data: {
      id: response.data,
    },
    meta: response.meta,
  };
}

export function transformReadSearchEnrichedFiltersResponse(
  response: any,
): ReadSearchEnrichedFiltersApiResponse {
  return {
    data: {
      filters: response.data.params.filters,
    },
    meta: response.meta,
  };
}

function transformSavedSearch(savedSearch: any): SavedSearch {
  return produce<SavedSearch>(savedSearch, (draft) => {
    draft.params.query = draft.params.query ?? '';

    draft.params.filters = (
      draft.params.filters
        ? enrichStaticFilters(draft.params.filters)
        : filtersInitialState
    ) as SearchFilters;
  });
}

/**
 * Transforms the search results attributes from a raw response.
 *
 * @param response The raw response.
 */
function transformSearchResultsAttributes(response: any) {
  response.data.forEach((hitsType) =>
    hitsType.hits.forEach((hit) => {
      if (typeof hit.initial_channel !== 'undefined') {
        hit.initial_channel = convertChannelCasing(
          hit.initial_channel,
          'snake',
        );
      }

      if (typeof hit.inboundmessagecount === 'string') {
        hit.inboundmessagecount = JSON.parse(hit.inboundmessagecount);
      }

      if (typeof hit.outboundmessagecount === 'string') {
        hit.outboundmessagecount = JSON.parse(hit.outboundmessagecount);
      }

      delete hit.document_type;
      delete hit.join_field;
    }),
  );
}
